import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { resetUser } from 'features/Authentication/user.slice'
import { ExamEndedCode, IExamQuestion, IExamResult } from 'Services/examService'

export interface IExamSession {
  examId: string
  totalQuestions: number
  questions: IExamQuestion[]
  // Absolute end time (epoch ms). The countdown is derived from it, never decremented.
  deadline: number
}

export interface IStartExamPayload extends IExamSession {
  // Answers already saved on the server, restored when an exam is resumed.
  answers?: Record<string, string>
}

export type IExamSubmission = IExamResult

export interface IExamState {
  session: IExamSession | null
  answers: Record<string, string>
  submission: IExamSubmission | null
  // Set when the server ended the exam (logout, new login, deadline); it never accepts answers again.
  ended: { code: ExamEndedCode; message: string } | null
}

const initialState: IExamState = {
  session: null,
  answers: {},
  submission: null,
  ended: null,
}

const examSlice = createSlice({
  name: 'examSlice',
  initialState,
  reducers: {
    startExamSession: (state, action: PayloadAction<IStartExamPayload>) => {
      const { answers, ...session } = action.payload
      state.session = session
      state.answers = answers ?? {}
      state.submission = null
      state.ended = null
    },
    selectExamAnswer: (state, action: PayloadAction<{ questionId: string; optionId: string }>) => {
      // Answers are frozen once the exam is submitted.
      if (state.submission) return
      state.answers[action.payload.questionId] = action.payload.optionId
    },
    markExamSubmitted: (state, action: PayloadAction<IExamSubmission>) => {
      state.submission = action.payload
    },
    markExamEnded: (state, action: PayloadAction<{ code: ExamEndedCode; message: string }>) => {
      state.ended = action.payload
    },
    resetExam: () => initialState,
  },
  // Logging out (or a 401) drops the exam; an old exam is never restored after the next login.
  extraReducers: (builder) => {
    builder.addCase(resetUser, () => initialState)
  },
})

export const { startExamSession, selectExamAnswer, markExamSubmitted, markExamEnded, resetExam } =
  examSlice.actions
export const examReducer = examSlice.reducer
